import {
  Address,
  ApplicationVariablesDto,
  DashboardAdditionalInfo,
  MissingEmploymentInformation,
} from 'prequalificationV2/api/ApplicationApi';
import { BorrowerRole } from 'enums/BorrowerRole';
import {
  EMPLOYMENT_SECTION_AMOUNT,
  MAP_INCOME_TYPE_TO_EMPLOYMENT_VARIABLE_LABELS,
} from 'prequalificationV2/pages/Dashboard/steps/InformationTabs/AdditionalInformationTab/additionalInformation.constants';
import { getStreetAddress } from 'prequalificationV2/utils/stringUtils';
import { CurrentEmploymentVariable } from 'prequalificationV2/pages/Dashboard/steps/InformationTabs/AdditionalInformationTab/additionalInformation.enums';

export const getEmploymentVariableName = (
  variableKey: string,
  borrowerType: BorrowerRole = BorrowerRole.Borrower,
  index: number = 1,
) => {
  const postfix = index === 1 ? '' : `_${index}`;

  return `${borrowerType === BorrowerRole.Borrower ? '' : 'co'}${variableKey}${postfix}`;
};

export const getEmploymentsAmount = (borrowerType: BorrowerRole, variables: any): number => {
  let result = 0;

  for (let index = 1; index <= EMPLOYMENT_SECTION_AMOUNT; index++) {
    if (!variables[getEmploymentVariableName(CurrentEmploymentVariable.EmployerName, borrowerType, index)]) {
      return index - 1;
    }

    result = index;
  }

  return result;
};

export const getIsAddressInformationDisabled = (address: Address): boolean => {
  return Object.values(address).every((value) => !!value);
};

export const getIsInitialValueNotNull = (initialValue: any): boolean => {
  return !!initialValue;
};

export const getMissingEmploymentInformation = (
  dashboardAdditionalInfo: DashboardAdditionalInfo,
  borrowerType: BorrowerRole,
): MissingEmploymentInformation[] => {
  return (
    (borrowerType === BorrowerRole.Borrower
      ? dashboardAdditionalInfo.borrowerMissingEmploymentInformation
      : dashboardAdditionalInfo.coborrowerMissingEmploymentInformation) || []
  );
};

export const getIsMissingEmploymentInformationCompleted = (
  missingEmploymentInformationList: MissingEmploymentInformation[],
): boolean => {
  return missingEmploymentInformationList.every(
    (missingEmploymentInformation) => missingEmploymentInformation.isCompleted,
  );
};

export const getIsEmploymentSectionCompleted = (
  dashboardAdditionalInfo: DashboardAdditionalInfo,
  borrowerType: BorrowerRole,
): boolean => {
  if (dashboardAdditionalInfo.isMissingEmploymentInformationRequired) {
    return getIsMissingEmploymentInformationCompleted(
      getMissingEmploymentInformation(dashboardAdditionalInfo, borrowerType),
    );
  }

  const {
    isBorrowerCurrentEmploymentsRequired,
    isPartialBorrowerCurrentEmploymentsRequired,
    isBorrowerCurrentEmploymentsCompleted,
    isBorrowerPreviousEmploymentRequired,
    isBorrowerPreviousEmploymentCompleted,
    isBorrowerEmploymentStatusInformationRequired,
    isBorrowerEmploymentStatusInformationCompleted,
    isCoBorrowerCurrentEmploymentsRequired,
    isPartialCoBorrowerCurrentEmploymentsRequired,
    isCoBorrowerCurrentEmploymentsCompleted,
    isCoBorrowerPreviousEmploymentRequired,
    isCoBorrowerPreviousEmploymentCompleted,
    isCoBorrowerEmploymentStatusInformationRequired,
    isCoBorrowerEmploymentStatusInformationCompleted,
  } = dashboardAdditionalInfo;

  const isBorrower = borrowerType === BorrowerRole.Borrower;
  const isCurrentEmploymentsRequired = isBorrower
    ? isBorrowerCurrentEmploymentsRequired || isPartialBorrowerCurrentEmploymentsRequired
    : isCoBorrowerCurrentEmploymentsRequired || isPartialCoBorrowerCurrentEmploymentsRequired;

  const isCurrentEmploymentsCompleted = isBorrower
    ? isBorrowerCurrentEmploymentsCompleted
    : isCoBorrowerCurrentEmploymentsCompleted;

  const isPreviousEmploymentRequired = isBorrower
    ? isBorrowerPreviousEmploymentRequired
    : isCoBorrowerPreviousEmploymentRequired;

  const isPreviousEmploymentCompleted = isBorrower
    ? isBorrowerPreviousEmploymentCompleted
    : isCoBorrowerPreviousEmploymentCompleted;

  const isEmploymentStatusInformationRequired = isBorrower
    ? isBorrowerEmploymentStatusInformationRequired
    : isCoBorrowerEmploymentStatusInformationRequired;

  const isEmploymentStatusInformationCompleted = isBorrower
    ? isBorrowerEmploymentStatusInformationCompleted
    : isCoBorrowerEmploymentStatusInformationCompleted;

  return (
    (isCurrentEmploymentsRequired ? isCurrentEmploymentsCompleted : true) &&
    (isPreviousEmploymentRequired ? isPreviousEmploymentCompleted : true) &&
    (isEmploymentStatusInformationRequired ? isEmploymentStatusInformationCompleted : true)
  );
};

export const getEmploymentVariableValue = (
  applicationVariables: ApplicationVariablesDto,
  variable: CurrentEmploymentVariable,
  borrowerType: BorrowerRole,
  index: number,
): string => {
  const getApplicationVariableValue = (employmentVariable: CurrentEmploymentVariable) => {
    return applicationVariables[
      getEmploymentVariableName(employmentVariable, borrowerType, index) as keyof ApplicationVariablesDto
    ];
  };

  const address = (getApplicationVariableValue(CurrentEmploymentVariable.Address) as Address | null) || {};

  switch (variable) {
    case CurrentEmploymentVariable.Address:
      return address.street_name ? getStreetAddress(address.street_number || '', address.street_name) : '';
    case CurrentEmploymentVariable.City:
      return address.city || '';
    case CurrentEmploymentVariable.State:
      return address.state_or_province || '';
    case CurrentEmploymentVariable.Zip:
      return address.zip_or_postal_code || '';
    default:
      return (getApplicationVariableValue(variable) as string) || '';
  }
};

export const getMissingEmploymentInformationDefaultValues = (
  applicationVariables: ApplicationVariablesDto,
  missingEmploymentInformationList: MissingEmploymentInformation[],
  borrowerType: BorrowerRole,
): Record<string, string> => {
  return missingEmploymentInformationList.reduce((defaultValues, { index, incomeType }) => {
    const employmentVariables = Object.keys(
      MAP_INCOME_TYPE_TO_EMPLOYMENT_VARIABLE_LABELS[incomeType] || {},
    ) as CurrentEmploymentVariable[];

    return employmentVariables.reduce(
      (missingEmploymentInformationDefaultValues, variable) => ({
        ...missingEmploymentInformationDefaultValues,
        [getEmploymentVariableName(variable, borrowerType, index)]: getEmploymentVariableValue(
          applicationVariables,
          variable,
          borrowerType,
          index,
        ),
      }),
      defaultValues,
    );
  }, {});
};
